import { TypedDocumentNode, gql } from '@apollo/client';
import {
  faChevronDoubleDown,
  faChevronDoubleUp,
} from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames';
import { ComponentProps, useState } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled, { keyframes } from 'styled-components';

import { DivisionChange } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Scrollable } from '@sorare/core/src/atoms/navigation/Scrollable';
import { Video } from '@sorare/core/src/atoms/ui/Video';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';
import { glossary } from '@sorare/core/src/lib/glossary';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { FlowEndButton } from 'components/so5/LastFixtureStory/FlowEndButton';
import { GameweekDate } from 'components/so5/LastFixtureStory/GameweekDate';
import {
  DELAY_BEFORE_ANIMATION_MS,
  LeaderboardDivisionChangeRecap,
} from 'components/so5/LastFixtureStory/LeaderboardDivisionChangeRecap';

import { DivisionChangeStory_so5Fixture } from './__generated__/index.graphql';

const titleEnter = keyframes`
  from {
    opacity: 0;
    transform: translateY(30%);
  }
  to {
    opacity: 1;
    transform: translateY(0%);
  }
`;

const cardsEnter = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const contentsExit = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`;

const VideoPlayer = styled(Video)`
  position: absolute;
  inset: 0;
`;

export const TitleRow = styled(Horizontal)`
  padding: 0 var(--double-unit);
  gap: var(--double-unit);
  @media ${tabletAndAbove} {
    gap: 80px;
  }
`;
export const Title = styled.div`
  font-size: 32px;
  text-align: center;
  @media ${tabletAndAbove} {
    font-size: 56px;
  }
`;

const TitleWrapper = styled(Vertical).attrs({ gap: 1, center: true })`
  position: absolute;
  top: var(--quadruple-unit);
  @media ${tabletAndAbove} {
    top: 80px;
  }
  opacity: 0;
  animation: ${titleEnter} 0.5s ease-out ${DELAY_BEFORE_ANIMATION_MS}ms forwards;
`;

const Buttons = styled(Vertical)`
  min-width: 200px;
  position: absolute;
  bottom: var(--double-unit);
  opacity: 0;
  animation: ${titleEnter} 0.5s ease-out ${DELAY_BEFORE_ANIMATION_MS + 1800}ms
    forwards;
  @media ${tabletAndAbove} {
    gap: var(--triple-unit);
    bottom: var(--quadruple-unit);
  }
`;

const StyledGameweekDate = styled(GameweekDate)`
  --gameweek-date-bg: linear-gradient(90deg, #f23b27 0%, #9d190a 100%);

  &.isPromotion {
    --gameweek-date-bg: linear-gradient(90deg, #22ff60 0%, #14993a 100%);
  }
  --gameweek-date-color: var(--c-black);
`;

const Contents = styled(Vertical).attrs({ center: true })`
  isolation: isolate;
  justify-content: stretch;
  gap: calc(9 * var(--unit));
  height: 100%;
  padding: var(--quadruple-unit);
  &.isUnmounting {
    animation: ${contentsExit} 0.5s ease-out forwards;
  }
`;

const ScrollableWrapper = styled.div`
  min-width: 240px;
  max-width: 70vw;
  margin: auto;
  opacity: 0;
  animation: ${cardsEnter} 1s ease-out ${DELAY_BEFORE_ANIMATION_MS}ms forwards;
  @media ${tabletAndAbove} {
    max-width: 760px;
  }
`;

type Props = {
  so5Fixture: DivisionChangeStory_so5Fixture;
  onNext?: () => void;
  onClose: () => void;
  divisionChange: DivisionChange;
  introVideo: ComponentProps<typeof Video>['sources'];
  outroVideo: ComponentProps<typeof Video>['sources'];
};

const messages = defineMessages({
  promotionTitle: {
    defaultMessage: 'You got promoted',
    id: 'Promotion.promotionTitle',
  },
  relegationTitle: {
    defaultMessage: 'You can do better!',
    id: 'Promotion.relegationTitle',
  },
});

export const DivisionChangeStory = ({
  so5Fixture,
  onNext,
  onClose,
  divisionChange,
  introVideo,
  outroVideo,
}: Props) => {
  const isTabletAndAbove = useIsTabletAndAbove();
  const [isUnmounting, setIsUnmounting] = useState(false);

  const displayedRankings = so5Fixture.mySo5Rankings.filter(
    ranking => ranking.eligibleDivisionChange === divisionChange
  );
  const isPromotion = divisionChange === DivisionChange.PROMOTED;
  const icon = isPromotion ? faChevronDoubleUp : faChevronDoubleDown;
  const video = isUnmounting ? outroVideo : introVideo;

  const actionAfterOutro = (action?: () => void) => {
    setIsUnmounting(true);
    setTimeout(() => {
      action?.();
    }, 2500);
  };

  return (
    <>
      <VideoPlayer
        // remount video for the new one to play
        key={video[0]?.src}
        autoPlay
        muted
        playsInline
        sources={video}
      />
      <Contents className={classNames({ isUnmounting })}>
        <TitleWrapper>
          <StyledGameweekDate
            startDate={so5Fixture.startDate}
            endDate={so5Fixture.endDate}
            className={classNames({
              isPromotion,
            })}
          />
          <TitleRow>
            <FontAwesomeIcon
              icon={icon}
              color={
                isPromotion ? 'var(--c-score-high)' : 'var(--c-static-red-600)'
              }
              size="xl"
            />
            <Title>
              <FormattedMessage
                {...(isPromotion
                  ? messages.promotionTitle
                  : messages.relegationTitle)}
              />
            </Title>
            <FontAwesomeIcon
              icon={icon}
              size="xl"
              color={
                isPromotion ? 'var(--c-score-high)' : 'var(--c-static-red-600)'
              }
            />
          </TitleRow>
        </TitleWrapper>
        <ScrollableWrapper>
          <Scrollable
            itemToDisplay={Math.min(
              isTabletAndAbove ? 3 : 1,
              displayedRankings.length
            )}
            withMask
          >
            {displayedRankings.map(ranking => (
              <LeaderboardDivisionChangeRecap
                key={ranking.id}
                so5Ranking={ranking}
              />
            ))}
          </Scrollable>
        </ScrollableWrapper>
        <Buttons center>
          {onNext ? (
            <Button size="medium" color="secondary" fullWidth onClick={onNext}>
              <FormattedMessage {...glossary.next} />
            </Button>
          ) : (
            <FlowEndButton
              so5Fixture={so5Fixture}
              onClose={() => actionAfterOutro(onClose)}
              onClick={action => {
                actionAfterOutro(action);
              }}
            />
          )}
          <Button
            size="medium"
            color="tertiary"
            onClick={() => {
              actionAfterOutro(onClose);
            }}
          >
            <FormattedMessage {...glossary.close} />
          </Button>
        </Buttons>
      </Contents>
    </>
  );
};

DivisionChangeStory.fragments = {
  so5Fixture: gql`
    fragment DivisionChangeStory_so5Fixture on So5Fixture {
      slug
      startDate
      endDate
      mySo5Rankings {
        id
        eligibleDivisionChange
        ...LeaderboardDivisionChangeRecap_so5Ranking
      }
      ...FlowEndButton_so5Fixture
    }
    ${FlowEndButton.fragments.so5Fixture}
    ${LeaderboardDivisionChangeRecap.fragments.so5Ranking}
  ` as TypedDocumentNode<DivisionChangeStory_so5Fixture>,
};
